import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Target, Calendar, Lightbulb, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const dimensionColors = {
  Physical: '#38BDF8',
  Mental: '#818CF8',
  Emotional: '#C084FC',
  Financial: '#34D399',
  Career: '#FBBF24',
  Lifestyle: '#F472B6',
  Spiritual: '#A78BFA',
  'Self-Care': '#F59E0B',
  Self_care: '#F59E0B'
};

function Prescription() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [prescription, setPrescription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    // Check if coming from Stripe
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get('session_id');

    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      fetchPrescription();
    }
  }, [assessmentId, location]);

  const verifyPayment = async (sessionId) => {
    setVerifying(true);
    try {
      const response = await axios.get(
        `${API}/payment/verify/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.status === 'succeeded') {
        toast.success('Payment successful! Your wellness prescription is unlocked.');
        fetchPrescription();
      } else {
        toast.error('Payment verification failed');
        navigate(`/result/${assessmentId}`);
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error('Payment verification failed');
      navigate(`/result/${assessmentId}`);
    } finally {
      setVerifying(false);
    }
  };

  const fetchPrescription = async () => {
    try {
      const response = await axios.get(
        `${API}/prescription/${assessmentId}`,
        { withCredentials: true }
      );
      setPrescription(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching prescription:', error);
      if (error.response?.status === 403) {
        toast.error('Please unlock your wellness prescription first');
        navigate(`/result/${assessmentId}`);
      } else if (error.response?.status === 401) {
        toast.error('Please login to view your wellness prescription');
        navigate('/');
      } else {
        toast.error('Failed to load wellness prescription');
        navigate('/');
      }
    }
  };

  if (loading || verifying) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC]" data-testid="prescription-loading">
        <div className="spinner w-12 h-12 border-4 border-[#4F46E5] border-t-transparent rounded-full mb-4" />
        <p className="text-[#64748B]">
          {verifying ? 'Verifying payment...' : 'Loading your wellness prescription...'}
        </p>
      </div>
    );
  }

  const { lowest_dimension, lowest_score, prescription: plan } = prescription;
  const dimensionColor = dimensionColors[lowest_dimension] || '#4F46E5';

  return (
    <div className="min-h-screen relative overflow-hidden py-12 px-6" style={{ backgroundColor: '#F8FAFC' }} data-testid="prescription-page">
      {/* Background pattern */}
      <div className="absolute inset-0 pattern-dots z-0" />
      
      {/* Decorative gradients */}
      <div 
        className="absolute top-20 -left-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)' }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 fade-in">
          <div className="flex items-center justify-center gap-2 mb-4" data-testid="prescription-brand">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#4F46E5] to-[#6366F1] flex items-center justify-center shadow-soft">
              <span className="text-white text-[10px] font-bold">CW</span>
            </div>
            <span className="text-sm text-[#64748B] font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Clinity
            </span>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ECFDF5] text-[#10B981] text-sm font-semibold border border-[#A7F3D0] mb-4">
            <CheckCircle2 className="w-4 h-4" />
            Wellness Prescription Unlocked
          </div>
          <h1 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-[#0F172A]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Your Personalized <span className="gradient-text">Upgrade Plan</span>
          </h1>
          <p className="text-[#64748B]">Based on your Wellness Test results</p>
        </div>

        {/* Focus Area */}
        <div 
          className="bg-white border-2 rounded-3xl p-8 mb-8 fade-in shadow-soft-lg"
          style={{ borderColor: `${dimensionColor}40` }}
          data-testid="focus-area"
        >
          <div className="flex items-center gap-3 mb-4">
            <Target className="w-6 h-6" style={{ color: dimensionColor }} />
            <h2 className="text-2xl font-bold text-[#0F172A]">Priority Focus Area</h2>
          </div>
          <div className="flex items-baseline gap-4 mb-4">
            <span className="text-5xl font-bold" style={{ color: dimensionColor }}>
              {lowest_dimension}
            </span>
            <span className="text-2xl text-[#64748B]">Score: {lowest_score}/100</span>
          </div>
          <p className="text-[#475569] text-lg">{plan.root_cause}</p>
        </div>

        {/* 30-Day Plan */}
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-8 mb-8 fade-in shadow-soft" data-testid="30-day-plan">
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="w-6 h-6 text-[#4F46E5]" />
            <h2 className="text-2xl font-bold text-[#0F172A]">30-Day Transformation Plan</h2>
          </div>
          <p className="text-[#475569] leading-relaxed">{plan['30_day_plan']}</p>
        </div>

        {/* Habit Fixes */}
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-8 mb-8 fade-in shadow-soft" data-testid="habit-fixes">
          <div className="flex items-center gap-3 mb-6">
            <Lightbulb className="w-6 h-6 text-[#F59E0B]" />
            <h2 className="text-2xl font-bold text-[#0F172A]">Habit Protocols</h2>
          </div>
          <div className="space-y-4">
            {plan.habit_fixes.map((habit, index) => (
              <div 
                key={index} 
                className="flex items-start gap-3 p-4 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-all"
                data-testid={`habit-${index}`}
              >
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold"
                  style={{ backgroundColor: `${dimensionColor}15`, color: dimensionColor }}
                >
                  {index + 1}
                </div>
                <p className="text-[#475569]">{habit}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-8 mb-8 fade-in shadow-soft" data-testid="recommendations">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-6 h-6 text-[#EC4899]" />
            <h2 className="text-2xl font-bold text-[#0F172A]">Recommended Services</h2>
          </div>
          <div className="space-y-3">
            {plan.recommendations.map((rec, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-4 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]"
                data-testid={`recommendation-${index}`}
              >
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: dimensionColor }} />
                <p className="text-[#475569]">{rec}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center space-y-4">
          <p className="text-[#64748B]">
            Take action today. Your future self will thank you.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-gradient-to-r from-[#4F46E5] to-[#6366F1] hover:from-[#6366F1] hover:to-[#818CF8] text-white font-semibold rounded-xl transition-colors glow-primary"
              data-testid="home-btn"
            >
              Back to Home
            </button>
            <button
              onClick={() => navigate('/assessment')}
              className="px-8 py-3 bg-white border border-[#E2E8F0] hover:border-[#4F46E5] text-[#0F172A] font-semibold rounded-xl transition-colors shadow-soft"
              data-testid="retake-assessment-btn"
            >
              Retake in 30 Days
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Prescription;
